/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

/**
 * @module enervee_utils.balanced_columns
 */
function balancedColumnsDirective() {
    return {
        restrict: 'E',
        scope: {
            items: '<',
            itemNameProp: '@',
            iconConfig: '<',
            numColumns: '<',
            onItemClicked: '&',
        },
        controllerAs: 'vm',
        bindToController: true,
        controller: balancedColumnsController,
        template: require('./balancedColumnsView.html')
    };
}

balancedColumnsController.$inject = ['$scope', 'CategoriesService', 'UtilService'];
function balancedColumnsController($scope, CategoriesService, UtilService) {
    const vm = this;
    vm.sortItemsIntoColumns = sortItemsIntoColumns;
    vm.getItemName = getItemName;
    vm.columnList = [];

    let flatPageSlugs = Object.keys(UtilService.getSiteSetting('category_landing_pages_info_v2', {})) || [];
    vm.$onInit = () => {
        sortItemsIntoColumns(vm.items, vm.numColumns);
        // If the bound items changes, resort the columns
        $scope.$watch('vm.items', () => {
            sortItemsIntoColumns(vm.items, vm.numColumns);
        });
        // If the number of columns changes, resort the columns
        $scope.$watch('vm.numColumns', (newVal, oldVal) => {
            if (newVal === oldVal) return;
            sortItemsIntoColumns(vm.items, vm.numColumns);
        });
     };

    /**
     * Takes a map of groups of items and breaks them into 3 separate columns for display.
     * Based on the numColumns variable, will break the items into N number of
     * columns. Updates `vm.columnList` in place with the result.
     *
     * @param {Object} groupMap A map of item lists by their group name
     * @param {Boolean} [numColumns=3]
     * @returns {Array<Array<Object>>}
     */
    function sortItemsIntoColumns(groupMap, numColumns=3) {
        let groups = getListFromGroupMap(groupMap).sort(largestToSmallestGroup);
        // Get our arrays ready for columns
        const columnList = createArrayWithSubarrays(numColumns);
        if (UtilService.isFeatureEnabled('special_offers_page')) {
            // we want this at the top of the first column always if enabled.
            columnList[0].push({
                name: 'Special Offers',
                is_parent: true,
                route: 'special-offers',
                category_code: 'special-offers'
            });
        }
        // Let's see how many items we're aiming for in each column
        let totalItems = getTotalGroupItemsCount(groups);
        const targetNumPerColumn = Math.ceil(totalItems / numColumns);
        const displayedGroupNames = [];
        // For each group, step through each column until break
        forEachGroupAndColumn(groups, columnList, (group, column, nextGroup) => {
            const potentialLength = column.length + getGroupSize(group);

            // If the column is empty and the potential length exceeds the target number per column
            const emptyColumnAndGreaterThanTarget = column.length === 0 && potentialLength > targetNumPerColumn;
            // If the potential length is less than or equal to the target number per column
            const lessThanOrEqualToTarget = potentialLength <= targetNumPerColumn;

            if (emptyColumnAndGreaterThanTarget || lessThanOrEqualToTarget) {
                // Either this group fits no problem, or this group is bigger
                // than our target length and it's an empty column. Add em.
                pushGroupToColumn(group, column, displayedGroupNames);
                nextGroup();
            }
        });

        // Now let's check to make sure all the groups got sorted
        groups
            .filter(parent => !displayedGroupNames.includes(parent.name))
            .forEach((parent) => {
                // This one didn't make it in a column. add it to the first column.
                pushGroupToColumn(parent, columnList[0], displayedGroupNames)
            });
        vm.columnList = columnList;
    }
    
    /**
     * 
     * @param {Array<Object>} groups 
     * @param {Array<Array<Object>>} columns 
     * @param {Function} callback A function that takes `group`, `column`, and `nextGroup`
     *
     *
     */
    function forEachGroupAndColumn(groups, columns, callback) {
        groups.forEach((group) => {
            for (let i=0; i<columns.length; i++) {
                let nextGroupCalled = false;
                const nextGroup = () => nextGroupCalled = true; 
                const column = columns[i];
                callback(group, column, nextGroup);
                if (nextGroupCalled) break;
            }
        });
    }
    
    /**
     * Returns a new array with the given number of subarrays.
     *
     * @param {Number} numberOfSubarrays 
     */
    function createArrayWithSubarrays(numberOfSubarrays) {
        return Array(numberOfSubarrays).fill().map(() => []);
    }

    /**
     * Counts all of the groups and their children.
     *
     * @param {Array<Object>} groups 
     * @param {Array<Object>} groups[].children 
     * @returns {Number}
     */
    function getTotalGroupItemsCount(groups) {
        return groups.reduce((sum, group) => sum + getGroupSize(group), 0);
    }

    /**
     * Returns the size of the group.
     *
     * @param {Object} group 
     * @returns {Number}
     */
    function getGroupSize(group) {
        // Add 1 because the group name is counted in the length
        return group.children.length + 1;
    }

    /**
     * Pushes the group and all its children to the column 
     * and makes sure the group name is recorded.
     *
     * @param {Object} group
     * @param {Array<Object>} column 
     * @param {Array<String>} displayedGroupNames 
     */
    function pushGroupToColumn(group, column, displayedGroupNames) {
        displayedGroupNames.push(group.name);
        column.push(getGroupParentItem(group), ...group.children);

    }

    /**
     * Returns a faux-item that acts as the parent of a group.
     *
     * @param {Object} group 
     * @param {String} group.name
     * @returns {Object}
     */
    function getGroupParentItem(group) {
        const parentObj = {name: group.name, is_parent: true, route: 'categories'};
        const slugifiedCode = CategoriesService.getParentCategorySlug(group.name);
        if (flatPageSlugs.includes(slugifiedCode)) {
            parentObj.category_code = slugifiedCode;
        }
        return parentObj;
    }

    /**
     * Gets an item's name, based off of vm.itemNameProp.
     * If vm.itemNameProp is not set, returns item.name.
     *
     * @param {Object} item 
     * @returns {*}
     */
    function getItemName(item) {
        const key = vm.itemNameProp || 'name';
        return item[key];
    }

    /**
     * Sorting function that handles sorting lists of groups from
     * the largest to the smallest.
     *
     * @param {Object} groupA
     * @param {Array<Object>} groupA.children
     * @param {Object} groupB
     * * @param {Array<Object>} groupB.children
     */
    function largestToSmallestGroup(groupA, groupB) {
        return groupB.children.length - groupA.children.length;
    }

    /**
     * Takes a groupMap object and returns a list of group objects
     *
     * @param {Object} groupMap A map of item lists by their group name
     * @returns {Array<Object>} Array of group objects
     */
    function getListFromGroupMap(groupMap=[]) {
        if (groupMap === null) return [];
        return Object.keys(groupMap)
            .map(key => [key, groupMap[key]])
            .map(([ name, children ]) => ({ name, children }));
    }
}

module.exports = balancedColumnsDirective;